// Tracking.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar'; // Importa el Sidebar
import { Button } from '../components/ui/button';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import axios from 'axios';
import Topbar from '../components/Topbar';
import { getShipmentComments, addShipmentComment } from '../api';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

const formatDateTime = (dateString) => {
  if (!dateString) return 'Pendiente';
  return new Date(dateString).toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const getDeliveryStatusColor = (order) => {
  if (!order.metrics?.order_Delivery) return 'bg-gray-300'; // Pendiente
  
  switch (order.order_Substatus) {
    case 'RECHAZADO':
      return 'bg-red-500';
    case 'PARCIAL':
      return 'bg-yellow-500';
    case 'ENTREGADO':
      return 'bg-green-500';
    default:
      return 'bg-gray-300';
  }
};

const getDeliveryTimeStatus = (etaDestino, ataDestino) => {
  const now = new Date();
  const eta = etaDestino ? new Date(etaDestino) : null;
  const ata = ataDestino ? new Date(ataDestino) : null;

  if (!eta) return { status: 'N/A', color: 'text-gray-500' };

  if (ata) {
    // Si ya se entregó, comparar ETA con ATA
    return ata > eta 
      ? { status: 'Retrasado', color: 'text-red-500' }
      : { status: 'En Tiempo', color: 'text-green-500' };
  } else {
    // Si aún no se entrega, comparar ETA con tiempo actual
    return now > eta
      ? { status: 'Retrasado', color: 'text-red-500' }
      : { status: 'En Tiempo', color: 'text-green-500' };
  }
};

const TrackingTimeline = ({ shipment }) => {
  if (!shipment || !shipment.orders) return null;
  
  let allEvents = [];
  const sortedOrders = [...shipment.orders].sort((a, b) => (a.Stop || 0) - (b.Stop || 0));

  // Verificar si el embarque tiene múltiples orígenes
  const hasMultipleOrigins = shipment.loading_sequence && 
    Object.keys(shipment.loading_sequence).length > 1;
  
  if (hasMultipleOrigins) {
    // 1. Evento inicial - Se agruparon órdenes
    const firstOrder = sortedOrders[0];
    allEvents.push({
      id: 'orders_grouped',
      action: 'Se agruparon órdenes',
      timestamp: formatDateTime(firstOrder?.metrics?.order_Agroupdt),
      status: !firstOrder?.metrics?.order_Agroupdt ? 'pending' : 'completed'
    });

    // Procesamos cada origen según el loading_sequence
    const originNames = Object.keys(shipment.loading_sequence);
    
    // Ordenamos los orígenes por su secuencia
    const sortedOrigins = originNames.sort((a, b) => 
      shipment.loading_sequence[a] - shipment.loading_sequence[b]
    );
    
    // Para cada origen, procesamos sus eventos
    sortedOrigins.forEach(originName => {
      const sequence = shipment.loading_sequence[originName];
      
      // Encontrar órdenes de este origen
      const ordersFromOrigin = shipment.orders.filter(order => order.loading_order === sequence);
      
      if (ordersFromOrigin.length === 0) {
        return; // Continuar con el siguiente origen
      }
      
      // Agregar encabezado de origen
      allEvents.push({
        id: `origin_header_${sequence}`,
        action: `Origen: ${originName}`,
        subtitle: `Fase de carga ${sequence} de ${shipment.total_loading_phases}`,
        timestamp: `${ordersFromOrigin.length} ${ordersFromOrigin.length === 1 ? 'orden' : 'órdenes'}`,
        isHeader: true,
        originPhase: true
      });
      
      // Para métricas, tomamos la primera orden del origen como referencia
      const referenceOrder = ordersFromOrigin[0];
      
      // Eventos de carga en este origen
      allEvents.push({
        id: `origin_arrival_${sequence}`,
        action: 'Llegada a Origen',
        timestamp: formatDateTime(referenceOrder?.metrics?.order_OriginATA),
        status: !referenceOrder?.metrics?.order_OriginATA ? 'pending' : 'completed'
      });
      
      allEvents.push({
        id: `loading_start_${sequence}`,
        action: 'Inicio de Carga',
        timestamp: formatDateTime(referenceOrder?.metrics?.order_Origin_chargeinit),
        status: !referenceOrder?.metrics?.order_Origin_chargeinit ? 'pending' : 'completed'
      });
      
      allEvents.push({
        id: `loading_end_${sequence}`,
        action: 'Fin de Carga',
        timestamp: formatDateTime(referenceOrder?.metrics?.order_Origin_chargefin),
        status: !referenceOrder?.metrics?.order_Origin_chargefin ? 'pending' : 'completed'
      });
      
      // Si es el último origen, agregar salida a ruta
      if (sequence === shipment.total_loading_phases) {
        allEvents.push({
          id: 'origin_departure',
          action: 'Salida a Ruta',
          timestamp: formatDateTime(referenceOrder?.metrics?.order_OriginATD),
          status: !referenceOrder?.metrics?.order_OriginATD ? 'pending' : 'completed'
        });
      }
    });
  } else {
    // Caso de un solo origen
    const firstOrder = sortedOrders[0];
    
    // Evento inicial - Se agruparon órdenes
    if (firstOrder?.metrics?.order_Agroupdt) {
      allEvents.push({
        id: 'orders_grouped',
        action: 'Se agruparon órdenes',
        timestamp: formatDateTime(firstOrder.metrics.order_Agroupdt),
        status: 'completed'
      });
    }
    
    // Añadir eventos de origen incluso si no tienen todas las métricas
    // Llegada a origen
    allEvents.push({
      id: 'origin_arrival',
      action: 'Llegada a Origen',
      subtitle: shipment.current_origin || shipment.origin,
      timestamp: formatDateTime(firstOrder?.metrics?.order_OriginATA) || 'No registrado',
      status: firstOrder?.metrics?.order_OriginATA ? 'completed' : 'pending'
    });
    
    // Inicio de carga
    allEvents.push({
      id: 'loading_start',
      action: 'Inicio de Carga',
      subtitle: shipment.current_origin || shipment.origin,
      timestamp: formatDateTime(firstOrder?.metrics?.order_Origin_chargeinit) || 'No registrado',
      status: firstOrder?.metrics?.order_Origin_chargeinit ? 'completed' : 'pending'
    });
    
    // Fin de carga
    allEvents.push({
      id: 'loading_end',
      action: 'Fin de Carga',
      subtitle: shipment.current_origin || shipment.origin,
      timestamp: formatDateTime(firstOrder?.metrics?.order_Origin_chargefin) || 'No registrado',
      status: firstOrder?.metrics?.order_Origin_chargefin ? 'completed' : 'pending'
    });
    
    // Salida a ruta
    allEvents.push({
      id: 'origin_departure',
      action: 'Salida a Ruta',
      subtitle: shipment.current_origin || shipment.origin,
      timestamp: formatDateTime(firstOrder?.metrics?.order_OriginATD) || 'No registrado',
      status: firstOrder?.metrics?.order_OriginATD ? 'completed' : 'pending'
    });
  }

  // 2. Eventos por parada (igual para ambos casos)
  const maxStops = Math.max(...sortedOrders.map(order => order.Stop || 0));
  
  for (let stopNumber = 1; stopNumber <= maxStops; stopNumber++) {
    const ordersInStop = sortedOrders.filter(order => order.Stop === stopNumber);
    if (ordersInStop.length === 0) continue;

    const firstOrderInStop = ordersInStop[0];
    
    // Encabezado de la parada
    allEvents.push({
      id: `stop_${stopNumber}`,
      action: `Parada ${stopNumber}`,
      subtitle: `${firstOrderInStop.City}, ${firstOrderInStop.State}`,
      timestamp: `${ordersInStop.length} ${ordersInStop.length === 1 ? 'orden' : 'órdenes'}`,
      isHeader: true
    });

    // Eventos de la parada
    if (firstOrderInStop.metrics?.order_ClientATA) {
      allEvents.push({
        id: `arrival_${stopNumber}`,
        action: 'Llegada a Cliente',
        timestamp: formatDateTime(firstOrderInStop.metrics.order_ClientATA),
        status: 'completed'
      });
    }

    if (firstOrderInStop.metrics?.order_Clientramp) {
      allEvents.push({
        id: `ramp_${stopNumber}`,
        action: 'Descargando',
        timestamp: formatDateTime(firstOrderInStop.metrics.order_Clientramp),
        status: 'completed'
      });
    }

    ordersInStop.forEach(order => {
      if (order.metrics?.order_Delivery) {
        allEvents.push({
          id: `delivery_${order.Order_number}`,
          action: `Entrega ${order.Order_number}`,
          timestamp: formatDateTime(order.metrics.order_Delivery),
          status: 'completed',
          isSubEvent: true,
          deliveryStatus: order.order_Substatus // Añadir el subestatus
        });
      }
    });
  }

  // 3. Eventos finales (POD, devolución y reentrega)
  // POD (si todas las órdenes tienen el mismo valor)
  const podTime = sortedOrders[0]?.metrics?.order_Podtime;
  if (podTime && sortedOrders.every(order => order.metrics?.order_Podtime === podTime)) {
    allEvents.push({
      id: 'pod',
      action: 'POD',
      timestamp: formatDateTime(podTime),
      status: 'completed'
    });
  }

  // Devolución (si alguna orden tiene eventos de devolución)
  const hasDevolution = sortedOrders.some(order => 
    order.metrics?.dev_route_dt || order.metrics?.devolucion_dt
  );

  if (hasDevolution) {
    const devolutionEvents = [
      {
        id: 'dev_route',
        action: 'En Ruta Devolución',
        timestamp: formatDateTime(sortedOrders.find(o => o.metrics?.dev_route_dt)?.metrics?.dev_route_dt),
        status: 'completed'
      },
      {
        id: 'devolution',
        action: 'Devuelto',
        timestamp: formatDateTime(sortedOrders.find(o => o.metrics?.devolucion_dt)?.metrics?.devolucion_dt),
        status: 'completed'
      }
    ];
    allEvents.push(...devolutionEvents);
  }

  // Reentrega (si alguna orden tiene eventos de reentrega)
  const hasRedelivery = sortedOrders.some(order => 
    order.metrics?.reentrega_dt || order.metrics?.reentregaATA || order.metrics?.reentregaRamp
  );

  if (hasRedelivery) {
    const redeliveryEvents = [
      {
        id: 'redelivery_arrival',
        action: 'Llegada a Cliente (Reentrega)',
        timestamp: formatDateTime(sortedOrders.find(o => o.metrics?.reentregaATA)?.metrics?.reentregaATA),
        status: 'completed'
      },      {
        id: 'redelivery_ramp',
        action: 'Descargando (Reentrega)',
        timestamp: formatDateTime(sortedOrders.find(o => o.metrics?.reentregaRamp)?.metrics?.reentregaRamp),
        status: 'completed'
      },
      {
        id: 'redelivery',
        action: 'Entrega (Reentrega)',
        timestamp: formatDateTime(sortedOrders.find(o => o.metrics?.reentrega_dt)?.metrics?.reentrega_dt),
        status: 'completed'
      }
    ];
    allEvents.push(...redeliveryEvents);
  }

  return (
    <div className="relative">
      <div className="absolute left-5 top-0 h-full w-0.5 bg-gray-300" />
      <div className="space-y-6 pl-8">
        {allEvents.length === 0 ? (
          <div className="text-gray-500 text-center">No hay eventos disponibles para este embarque</div>
        ) : (
          allEvents.map((event) => (
            <div key={event.id} 
                className={`relative flex items-start ${event.isSubEvent ? 'ml-6' : ''}`}>
              <div 
                className={`absolute left-0 w-4 h-4 rounded-full transform -translate-x-1/2 
                  ${event.isHeader && event.originPhase
                    ? 'bg-purple-500'  // Color especial para encabezados de origen
                    : event.isHeader 
                      ? 'bg-blue-500' 
                      : event.isRedelivery
                        ? 'bg-yellow-500'
                        : event.isSubEvent && event.deliveryStatus
                          ? event.deliveryStatus === 'RECHAZADO'
                            ? 'bg-red-500'
                            : event.deliveryStatus === 'PARCIAL'
                              ? 'bg-yellow-500'
                              : event.deliveryStatus === 'ENTREGADO'
                                ? 'bg-green-500'
                                : 'bg-gray-300'
                          : event.isSubEvent
                            ? 'w-3 h-3 bg-gray-400'
                            : event.status === 'completed' 
                              ? 'bg-green-500' 
                              : 'bg-gray-300'}`} 
              />
              <div className="ml-6">
                <p className={`font-semibold 
                  ${event.isHeader && event.originPhase
                    ? 'text-lg text-purple-600' // Color especial para texto de encabezados de origen
                    : event.isHeader 
                      ? 'text-lg text-blue-600' 
                      : event.isSubEvent
                        ? 'text-sm text-gray-600'
                        : 'text-base text-gray-800'}`}>
                  {event.action}
                </p>
                {event.subtitle && (
                  <p className="text-sm text-gray-600">{event.subtitle}</p>
                )}
                <p className={`text-sm 
                  ${event.isHeader && event.originPhase
                    ? 'text-purple-500' // Color especial para timestamp de encabezados de origen
                    : event.isHeader 
                      ? 'text-blue-500' 
                      : event.deliveryStatus === 'RECHAZADO'
                        ? 'text-red-500'
                        : event.deliveryStatus === 'PARCIAL'
                          ? 'text-yellow-500'
                          : event.status === 'completed' 
                            ? 'text-green-600' 
                            : 'text-gray-500'}`}>
                  {event.timestamp}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Componente para los comentarios
const CommentsSection = ({ shipmentId, comments, loadingComments, commentError, onAddComment }) => {
  const [newComment, setNewComment] = useState('');

  const handleSubmit = () => {
    if (newComment.trim()) {
      onAddComment(newComment);
      setNewComment('');
    }
  };

  return (
    <div className="bg-white p-6 shadow-md rounded h-full flex flex-col">
      <h3 className="text-xl font-bold text-gray-800 mb-4">Historial de Comentarios</h3>
      
      <div className="flex-grow mb-4 overflow-y-auto">
        {loadingComments ? (
          <div className="flex justify-center items-center h-24">
            <svg className="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : commentError ? (
          <div className="text-red-500 text-center">{commentError}</div>
        ) : comments.length === 0 ? (
          <div className="text-gray-500 text-center">No hay comentarios para este embarque</div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start">
                  <p className="font-semibold text-blue-600">{comment.user}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(comment.created_at).toLocaleString('es-MX', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <p className="text-gray-700 mt-1">{comment.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <textarea
          className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
          rows="3"
          placeholder="Escribe un comentario..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        ></textarea>
        <Button 
          onClick={handleSubmit}
          className="w-full"
          disabled={loadingComments || !newComment.trim()}
        >
          {loadingComments ? 'Enviando...' : 'Añadir Comentario'}
        </Button>
      </div>
    </div>
  );
};

const Tracking = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('tracking');
  const [shipmentData, setShipmentData] = useState(null); // Nuevo estado para almacenar los datos del embarque
  const [shipmentReference, setShipmentReference] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showSearch, setShowSearch] = useState(true); // Nuevo estado para controlar la visibilidad del buscador
  const [shipmentComments, setShipmentComments] = useState([]); // Estado para los comentarios
  const [loadingComments, setLoadingComments] = useState(false); // Estado para indicar carga de comentarios
  const [commentError, setCommentError] = useState(''); // Estado para errores de comentarios

  const navigate = useNavigate();

  // Parse URL query parameters when component mounts
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const shipmentParam = queryParams.get('shipment');
    
    if (shipmentParam) {
      setShipmentReference(shipmentParam);
      setSearchValue(shipmentParam);
      handleSearch(shipmentParam);
    }
  }, []);

  // Función para obtener los comentarios de un embarque
  const fetchComments = async (shipmentId) => {
    setLoadingComments(true);
    setCommentError('');
    try {
      const comments = await getShipmentComments(shipmentId);
      setShipmentComments(comments);
    } catch (error) {
      console.error('Error al cargar comentarios:', error);
      setCommentError('No se pudieron cargar los comentarios. Por favor, intente nuevamente.');
    } finally {
      setLoadingComments(false);
    }
  };

  // Función para añadir un nuevo comentario
  const handleAddComment = async (commentText) => {
    if (!commentText.trim() || !shipmentData?.id) return;

    setLoadingComments(true);
    setCommentError('');
    try {
      const userName = localStorage.getItem('userName') || 'Usuario';
      
      const addedComment = await addShipmentComment(shipmentData.id, {
        text: commentText,
        user: userName
      });
      
      // Actualizar lista de comentarios
      setShipmentComments(prev => [...prev, addedComment]);
    } catch (error) {
      console.error('Error al añadir comentario:', error);
      setCommentError('No se pudo añadir el comentario. Por favor, intente nuevamente.');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleSearch = async (reference = null) => {
    const searchRef = reference || shipmentReference;
    
    if (!searchRef.trim()) {
      setError('Por favor ingrese un folio de embarque');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Usar el nuevo endpoint específico para tracking
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/shipments/${searchRef}/tracking`);
      if (response.data) {
        //console.log('Datos del embarque:', response.data);
        
        // Procesar órdenes para obtener fechas de entrega
        const processedData = { ...response.data };
        
        // Si no hay fecha de entrega explícita, intentar extraerla de las órdenes
        if (!processedData.delivery_date && processedData.orders && processedData.orders.length > 0) {
          // Intentar obtener la fecha de entrega a partir de las citas de las órdenes
          const deliveryDates = processedData.orders
            .filter (order => order.Appointment)
            .map(order => {
              if (order.Appointment && order.Appointment_hr) {
                // Si tiene fecha y hora, combinarlas
                return new Date(`${order.Appointment.split('T')[0]}T${order.Appointment_hr}`);
              }
              return new Date(order.Appointment);
            })
            .filter(date => !isNaN(date.getTime())); // Filtrar fechas inválidas
            
          if (deliveryDates.length > 0) {
            // Usar la fecha más temprana como ETA de destino
            processedData.delivery_date = deliveryDates
              .sort((a, b) => a - b)[0]
              .toISOString();
          }
        }
        
        setShipmentData(processedData);
        setShowSearch(false); // Ocultar el buscador después de una búsqueda exitosa
        
        // Cargar comentarios si hay ID de embarque
        if (processedData.id) {
          fetchComments(processedData.id);
        }
      } else {
        setError('No se encontró el embarque');
        setShipmentData(null);
      }
    } catch (error) {
      console.error('Error al buscar el embarque:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Error al buscar el embarque. Por favor intente de nuevo.';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Error desconocido');
      setShipmentData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSearch = () => {
    setShowSearch(true);
    setShipmentData(null);
    setError('');
    setShipmentComments([]);
  };

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />

      <div className={`transition-all duration-300 flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}>
        <Topbar 
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            setShipmentReference(value);
          }}
        />

        {/* Buscador de embarques */}
        {showSearch && (
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md mx-auto mt-6">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Buscar Embarque</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Ingrese el folio del embarque"
                value={shipmentReference}
                onChange={(e) => {
                  setShipmentReference(e.target.value);
                  setSearchValue(e.target.value);
                }}
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <Button 
                onClick={() => handleSearch()} 
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
            </div>
          </div>
        )}

        {/* Contenido que se muestra solo cuando hay datos de embarque */}
        {shipmentData && (
          <div className="mt-6">
            {/* Botón para volver al buscador */}
            <div className="mb-4 flex justify-between items-center">
              <Button 
                onClick={handleBackToSearch} 
                variant="outline"
                className="flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Volver al buscador
              </Button>
              <div className="text-lg font-bold text-gray-700">
                Embarque: {shipmentData.reference}
              </div>
            </div>

            {/* Panel de datos del embarque - Estructura de grid mejorada */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Panel de información del embarque */}
              <div className="md:col-span-1 bg-white p-4 shadow-md rounded">
                <h3 className="text-lg font-bold mb-4">Detalles del Embarque</h3>
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold">Folio:</p>
                    <p className="text-sm">{shipmentData.reference}</p>
                  </div>
                  
                  {/* Sección Origen */}
                  <div className="border-t pt-4">
                    <p className="text-sm font-semibold mb-2">Origen:</p>
                    <p className="text-sm">{shipmentData.origin || 'N/A'}</p>
                    
                    {/* Para cada origen distinto (basado en loading_order) */}
                    {(() => {
                      // Agrupar órdenes por loading_order
                      const ordersByLoadingOrder = {};
                      
                      if (shipmentData.orders && shipmentData.orders.length > 0) {
                        shipmentData.orders.forEach(order => {
                          const loadingOrder = order.loading_order || 1;
                          if (!ordersByLoadingOrder[loadingOrder]) {
                            ordersByLoadingOrder[loadingOrder] = [];
                          }
                          ordersByLoadingOrder[loadingOrder].push(order);
                        });
                      }
                      
                      // Obtener las claves (loading_order) ordenadas
                      const loadingOrders = Object.keys(ordersByLoadingOrder)
                        .map(Number)
                        .sort((a, b) => a - b);
                      
                      // Si solo hay un origen, mantener el formato anterior
                      if (loadingOrders.length <= 1) {
                        return (
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <p className="text-xs font-semibold text-gray-600">ETA Origen:</p>
                              <p className="text-xs">
                                {formatDateTime(shipmentData.pickup_date) || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-600">ATA Origen:</p>
                              <div className="flex items-center gap-2">
                                <p className="text-xs">
                                  {formatDateTime(shipmentData.orders?.[0]?.metrics?.order_OriginATA) || 'N/A'}
                                </p>
                                {shipmentData.pickup_date && 
                                  shipmentData.orders?.[0]?.metrics?.order_OriginATA && (
                                    (() => {
                                      // Extraer solo fechas en formato YYYY-MM-DD para comparación
                                      let pickupDate = new Date(shipmentData.pickup_date);
                                      let ataDate = new Date(shipmentData.orders[0].metrics.order_OriginATA);
                                      
                                      // Verificar si hay error evidente en el año
                                      const currentYear = new Date().getFullYear();
                                      if (pickupDate.getFullYear() > currentYear + 5) {
                                        // Corregir año para comparación si es evidentemente incorrecto
                                        pickupDate.setFullYear(currentYear);
                                      }
                                      
                                      // Comparar las fechas corregidas
                                      return ataDate > pickupDate ? (
                                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
                                          Retraso
                                        </span>
                                      ) : null;
                                    })()
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // Para múltiples orígenes
                      return (
                        <div className="space-y-2 mt-2">
                          {loadingOrders.map(loadingOrder => {
                            // Obtener la primera orden del grupo para usar sus métricas
                            const ordersForThisOrigin = ordersByLoadingOrder[loadingOrder];
                            const referenceOrder = ordersForThisOrigin[0];
                            
                            // Determinar el nombre del origen real para este grupo
                            // Usar el Origin de la orden si está disponible
                            let originName = '';
                            if (referenceOrder.Origin) {
                              originName = referenceOrder.Origin;
                            } else if (loadingOrder === 1) {
                              originName = shipmentData.origin || 'Origen 1';
                            } else {
                              originName = `Origen ${loadingOrder}`;
                            }
                            
                            // Determinar ETA para este origen
                            let etaOrigen = null;
                            if (loadingOrder === 1) {
                              // Para el primer origen, usar pickup_date del embarque
                              etaOrigen = shipmentData.pickup_date;
                            } else {
                              // Para orígenes adicionales, calcular una fecha estimada
                              // basándonos en la fecha del primer origen y agregando horas
                              if (shipmentData.pickup_date) {
                                const baseDate = new Date(shipmentData.pickup_date);
                                // Añadir 4 horas por cada fase de carga anterior
                                const hoursToAdd = (loadingOrder - 1) * 6;
                                baseDate.setHours(baseDate.getHours() + hoursToAdd);
                                etaOrigen = baseDate.toISOString();
                              }
                            }
                            
                            // Obtener ATA para este origen
                            const ataOrigen = referenceOrder.metrics?.order_OriginATA;
                            
                            return (
                              <div key={loadingOrder} className="border border-gray-200 p-2 rounded">
                                <p className="text-xs font-semibold mb-1">Origen {loadingOrder}: {originName}</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600">ETA Origen {loadingOrder}:</p>
                                    <p className="text-xs">
                                      {formatDateTime(etaOrigen) || 'N/A'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600">ATA Origen {loadingOrder}:</p>
                                    <div className="flex items-center gap-1">
                                      <p className="text-xs">
                                        {formatDateTime(ataOrigen) || 'N/A'}
                                      </p>
                                      {etaOrigen && ataOrigen && (
                                        (() => {
                                          // Extraer solo fechas para comparación
                                          let pickupDate = new Date(etaOrigen);
                                          let ataDate = new Date(ataOrigen);
                                          
                                          // Verificar si hay error evidente en el año
                                          const currentYear = new Date().getFullYear();
                                          if (pickupDate.getFullYear() > currentYear + 5) {
                                            // Corregir año para comparación si es incorrecta
                                            pickupDate.setFullYear(currentYear);
                                          }
                                          
                                          // Comparar las fechas corregidas
                                          return ataDate > pickupDate ? (
                                            <span className="px-1 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
                                              Retraso
                                            </span>
                                          ) : null;
                                        })()
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Sección Destino */}
                  <div className="border-t pt-4">
                    <p className="text-sm font-semibold mb-2">Destino:</p>
                    <p className="text-sm">{shipmentData.destination || 'N/A'}</p>
                    
                    {/* Para cada destino distinto (basado en Stop) */}
                    {(() => {
                      // Agrupar órdenes por Stop
                      const ordersByStopNumber = {};
                      
                      if (shipmentData.orders && shipmentData.orders.length > 0) {
                        shipmentData.orders.forEach(order => {
                          const stopNumber = order.Stop || 1;
                          if (!ordersByStopNumber[stopNumber]) {
                            ordersByStopNumber[stopNumber] = [];
                          }
                          ordersByStopNumber[stopNumber].push(order);
                        });
                      }
                      
                      // Obtener las claves (Stop) ordenadas
                      const stopNumbers = Object.keys(ordersByStopNumber)
                        .map(Number)
                        .sort((a, b) => a - b);
                      
                      // Si solo hay un destino, mantener el formato anterior
                      if (stopNumbers.length <= 1) {
                        return (
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <p className="text-xs font-semibold text-gray-600">ETA Destino:</p>
                              <p className="text-xs">
                                {formatDateTime(shipmentData.delivery_date) || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-600">ATA Destino:</p>
                              <p className="text-xs">
                                {formatDateTime(shipmentData.orders?.[0]?.metrics?.order_ClientATA) || 'N/A'}
                              </p>
                            </div>
                          </div>
                        );
                      }
                      
                      // Para múltiples destinos
                      return (
                        <div className="space-y-2 mt-2">
                          {stopNumbers.map(stopNumber => {
                            // Obtener la primera orden del grupo para usar sus métricas
                            const ordersForThisStop = ordersByStopNumber[stopNumber];
                            const referenceOrder = ordersForThisStop[0];
                            
                            // Determinar el nombre del destino real para este grupo
                            const destinationName = referenceOrder.City && referenceOrder.State 
                              ? `${referenceOrder.City}, ${referenceOrder.State}`
                              : `Destino ${stopNumber}`;
                            
                            // Determinar ETA para este destino
                            let etaDestino = null;
                            if (referenceOrder.Appointment) {
                              if (referenceOrder.Appointment_hr) {
                                // Combinar fecha y hora
                                const datePart = referenceOrder.Appointment.split('T')[0];
                                etaDestino = `${datePart}T${referenceOrder.Appointment_hr}`;
                              } else {
                                // Usar solo la fecha
                                etaDestino = referenceOrder.Appointment;
                              }
                            } else if (stopNumber === 1 && shipmentData.delivery_date) {
                              // Para la primera parada, usar delivery_date del embarque si no hay Appointment
                              etaDestino = shipmentData.delivery_date;
                            }
                            
                            // Obtener ATA para este destino
                            const ataDestino = referenceOrder.metrics?.order_ClientATA;
                            
                            return (
                              <div key={stopNumber} className="border border-gray-200 p-2 rounded">
                                <p className="text-xs font-semibold mb-1">Parada {stopNumber}: {destinationName}</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600">ETA Destino {stopNumber}:</p>
                                    <p className="text-xs">
                                      {formatDateTime(etaDestino) || 'N/A'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600">ATA Destino {stopNumber}:</p>
                                    <div className="flex items-center gap-1">
                                      <p className="text-xs">
                                        {formatDateTime(ataDestino) || 'N/A'}
                                      </p>
                                      {etaDestino && ataDestino && (
                                        (() => {
                                          // Extraer solo fechas para comparación
                                          let etaDate = new Date(etaDestino);
                                          let ataDate = new Date(ataDestino);
                                          
                                          // Verificar si hay error evidente en el año
                                          const currentYear = new Date().getFullYear();
                                          if (etaDate.getFullYear() > currentYear + 5) {
                                            // Corregir año para comparación si es incorrecta
                                            etaDate.setFullYear(currentYear);
                                          }
                                          
                                          // Comparar las fechas corregidas
                                          return ataDate > etaDate ? (
                                            <span className="px-1 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
                                              Retraso
                                            </span>
                                          ) : null;
                                        })()
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Mapa y estado */}
              <div className="md:col-span-2 bg-white p-4 shadow-md rounded flex flex-col">
                <div className="flex flex-wrap md:flex-row justify-between items-start md:items-center mb-4 gap-3">
                  <div className="min-w-[120px]">
                    <h3 className="text-lg font-bold">Estatus</h3>
                    <p className="text-sm">{shipmentData.status}</p>
                  </div>
                  <div className="min-w-[120px]">
                    <h3 className="text-lg font-bold">Cumplimiento</h3>
                    {(() => {
                      const timeStatus = getDeliveryTimeStatus(
                        shipmentData.delivery_date,
                        shipmentData.orders?.[0]?.metrics?.order_ClientATA
                      );
                      return (
                        <p className={`text-xl font-semibold ${timeStatus.color}`}>
                          {timeStatus.status}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="min-w-[120px]">
                    <h3 className="text-lg font-bold">Sub Estatus</h3>
                    <p className="text-sm">{shipmentData.Substatus || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex-grow" style={{ height: '300px', position: 'relative' }}>
                  <MapContainer
                    center={[
                      shipmentData.last_latitude || 19.4326,
                      shipmentData.last_longitude || -99.1332
                    ]}
                    zoom={13}
                    style={{ position: 'absolute', height: '100%', width: '100%' }}
                    className="rounded"
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker position={[
                      shipmentData.last_latitude || 19.4326,
                      shipmentData.last_longitude || -99.1332
                    ]}>
                      <Popup>Unidad localizada aquí</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>

              {/* División 2/3 para Timeline y 1/3 para Comentarios */}
              <div className="md:col-span-2 bg-white p-6 shadow-md rounded max-h-[600px] overflow-y-auto">
                <h3 className="text-xl font-bold text-gray-800 mb-6">Seguimiento Logístico</h3>
                <TrackingTimeline shipment={shipmentData} />
              </div>
              
              {/* Sección de comentarios - 1/3 de la pantalla */}
              <div className="md:col-span-1 max-h-[600px]">
                <CommentsSection 
                  shipmentId={shipmentData.id}
                  comments={shipmentComments}
                  loadingComments={loadingComments}
                  commentError={commentError}
                  onAddComment={handleAddComment}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tracking;